import type { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Col, Form, Row } from "react-bootstrap";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import React from "react";
import type ExemptionDetailViewModel from "viewModels/callCenter/search/exemptions/detailExemption/exemptionDetailViewModel";
import ExemptionDetailWrapper from "./exemptionDetailWrapper";
import { formatDateTime } from "utils/localizations";
import { vehicleRegisterSynchronizationStatusToVariant } from "entities/vehicleRegisterSynchronizationStatus";
import { validUntilStatusToVariant } from "entities/validUntilStatus";
import ChargeChangeInitiator from "entities/chargeChangeInitiator";
import { emptyValue } from "utils/emptyValue";

const ExemptionDetailSection: ViewComponent<ExemptionDetailViewModel> = observer(({ vm }) => {
  const detail = vm.item.exemptionDetail;

  return (
    <ExemptionDetailWrapper title={vm.translate("exemptionDetail")}>
      <Row>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("vehicleResponse", "countryId")}</Form.Label>
          <FakeInput
            target={detail}
            property="vehicle"
            valueFormatter={props => (props.countryId ? vm.translateCountryId(props.countryId) : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("vehicleResponse", "licensePlate")}</Form.Label>
          <FakeInput
            target={detail}
            property="vehicle"
            valueFormatter={vehicle => (vehicle.licensePlate ? vehicle.licensePlate : emptyValue)}
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "validSince")}</Form.Label>
          <FakeInput
            target={detail}
            property="validSince"
            valueFormatter={value => (!value ? emptyValue : formatDateTime(value))}
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "validUntil")}</Form.Label>
          <FakeInput
            target={detail}
            property="validUntil"
            valueFormatter={value => {
              if (!value) {
                return emptyValue;
              }
              const formattedValue = formatDateTime(value);
              const variantValue = validUntilStatusToVariant(value);
              return variantValue === "default" ? (
                formattedValue
              ) : (
                <strong className={`text-${variantValue}`}>{formattedValue}</strong>
              );
            }}
          />
        </Form.Group>
        <Form.Group as={Col} md={12}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "exemptionReasonId")}</Form.Label>
          <FakeInput
            target={detail}
            property="exemptionReasonId"
            valueFormatter={value => (!value ? emptyValue : vm.translateExemptionReasonId(value))}
          />
        </Form.Group>

        <Form.Group as={Col} md={12}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "creationRecordId")}</Form.Label>
          <FakeInput
            target={detail}
            property="creationRecordId"
            valueFormatter={value => (!value ? emptyValue : value.toString())}
          />
        </Form.Group>
        <Form.Group as={Col} md={12}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "cancellationRecordId")}</Form.Label>
          <FakeInput
            target={detail}
            property="cancellationRecordId"
            valueFormatter={value => (!value ? emptyValue : value.toString())}
          />
        </Form.Group>
        <Form.Group as={Col} md={12}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "vehicleRegistrationChangeRecordId")}</Form.Label>
          <FakeInput
            target={detail}
            property="vehicleRegistrationChangeRecordId"
            valueFormatter={value => (!value ? emptyValue : value.toString())}
          />
        </Form.Group>
        <Form.Group as={Col} md={12}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "cancellationReason")}</Form.Label>
          <FakeInput
            target={detail}
            property="cancellationReason"
            valueFormatter={value => (!value ? emptyValue : value.toString())}
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "creationMethod")}</Form.Label>
          <FakeInput
            target={detail}
            property="creationMethod"
            valueFormatter={value => (!value ? emptyValue : vm.translateExemptionRegistrationMethod(value))}
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "cancellationMethod")}</Form.Label>
          <FakeInput
            target={detail}
            property="cancellationMethod"
            valueFormatter={value => (!value ? emptyValue : vm.translateExemptionRegistrationMethod(value))}
          />
        </Form.Group>
        <Form.Group as={Col} md={12}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "vehicleRegisterSynchronizationStatus")}</Form.Label>
          <FakeInput
            target={detail}
            property="vehicleRegisterSynchronizationStatus"
            valueFormatter={value => {
              const formattedValue = vm.translateVehicleRegisterSynchronizationStatus(value);
              return <strong className={`text-${vehicleRegisterSynchronizationStatusToVariant(value)}`}>{formattedValue}</strong>;
            }}
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "createdBy")}</Form.Label>
          <FakeInput
            target={detail}
            property="createdBy"
            valueFormatter={value =>
              !value ? vm.translateChargeChangeInitiator(ChargeChangeInitiator.System) : value.toString()
            }
          />
        </Form.Group>
        <Form.Group as={Col} md={6}>
          <Form.Label>{vm.translateSubAttribute("exemptionDetailResponse", "createdAt")}</Form.Label>
          <FakeInput
            target={detail}
            property="createdAt"
            valueFormatter={value => (!value ? emptyValue : formatDateTime(value))}
          />
        </Form.Group>
      </Row>
    </ExemptionDetailWrapper>
  );
});

export default ExemptionDetailSection;
